import {IndividualPlanHistoryChange} from '../../../../../../../backend/interfaces/individual-plan-history';

export type IndividualPlanHistorySummaryEntityType = 'plan' | 'diagnostic' | 'goal' | 'objective';

/**
 * One line of the summary of an history entry. It has no text: the component translates it.
 */
export type IndividualPlanHistorySummaryLine =
  | {
      type: 'created' | 'deleted';
      entityType: IndividualPlanHistorySummaryEntityType;
      count: number;
      // Definition of the element, only when a single element was created/deleted
      name?: string;
    }
  | {
      type: 'updated';
      entityType: IndividualPlanHistorySummaryEntityType;
      field: string;
      count: number;
      // Only when a single element changed and the field is a text
      oldValue?: string;
      newValue?: string;
    };

const TEXT_FIELDS = ['definition', 'observations'];
const MAX_TEXT_LENGTH = 60;

const isEntityType = (value: string): value is IndividualPlanHistorySummaryEntityType =>
  ['plan', 'diagnostic', 'goal', 'objective'].includes(value);

const truncate = (text: string): string =>
  text.length > MAX_TEXT_LENGTH ? `${text.slice(0, MAX_TEXT_LENGTH - 1)}…` : text;

const toText = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? truncate(value.trim()) : undefined;

const getDefinition = (value: unknown): string | undefined =>
  value && typeof value === 'object'
    ? toText((value as {definition?: unknown}).definition)
    : undefined;

/**
 * Summarizes what changed in an history entry. Elements created or deleted are grouped by type
 * (for example, "5 diagnostics created") and changed fields are grouped by element type and field.
 *
 * The creation of the plan itself is not summarized, as the type of the entry already says it.
 */
export function summarizeIndividualPlanChanges(
  changes: Array<IndividualPlanHistoryChange> | undefined
): Array<IndividualPlanHistorySummaryLine> {
  const lines: Array<IndividualPlanHistorySummaryLine> = [];
  const byKey = new Map<string, IndividualPlanHistorySummaryLine>();

  for (const change of changes ?? []) {
    if (!isEntityType(change.entityType)) {
      continue;
    }
    const entityType = change.entityType;

    if (change.kind === 'created' || change.kind === 'deleted') {
      if (entityType === 'plan') {
        continue;
      }
      const key = `${change.kind}:${entityType}`;
      const existing = byKey.get(key);
      if (existing && existing.type !== 'updated') {
        existing.count += 1;
        // A name only makes sense for a single element
        delete existing.name;
      } else {
        const line: IndividualPlanHistorySummaryLine = {
          type: change.kind,
          entityType,
          count: 1,
          name: getDefinition(change.kind === 'created' ? change.newValue : change.oldValue),
        };
        byKey.set(key, line);
        lines.push(line);
      }
    } else if (change.kind === 'updated' && change.field) {
      const key = `updated:${entityType}:${change.field}`;
      const existing = byKey.get(key);
      if (existing && existing.type === 'updated') {
        existing.count += 1;
        delete existing.oldValue;
        delete existing.newValue;
      } else {
        const isText = TEXT_FIELDS.includes(change.field);
        const line: IndividualPlanHistorySummaryLine = {
          type: 'updated',
          entityType,
          field: change.field,
          count: 1,
          oldValue: isText ? toText(change.oldValue) : undefined,
          newValue: isText ? toText(change.newValue) : undefined,
        };
        byKey.set(key, line);
        lines.push(line);
      }
    }
  }

  return lines;
}
